import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../auth/api';
import { API_BASE } from '../../config';
import { useAuth } from '../../auth/useAuth';
import EntryHeader from '../../components/wiki/EntryHeader';
import EntrySection from '../../components/wiki/EntrySection';
import StaleIndicator from '../../components/wiki/StaleIndicator';
import MarkdownView from '../../components/wiki/MarkdownView';
import ClientFitPanel from '../../components/wiki/ClientFitPanel';
import CompliancePanel from '../../components/wiki/CompliancePanel';
import VendorCard from '../../components/wiki/VendorCard';
import RelatedEntries from '../../components/wiki/RelatedEntries';
import TagChips from '../../components/wiki/TagChips';
import RoleGate from '../../auth/RoleGate';
import ExportPdfButton from '../../components/wiki/ExportPdfButton';
import { BookOpen, Zap, Users, Shield, MapPin, FileEdit } from 'lucide-react';

export default function WikiEntry() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { lang, hasRole } = useAuth();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const viewRecorded = useRef(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setError('');
        api.get(`/wiki/entries/${slug}`)
            .then((res) => setEntry(res.data?.entry || res.data))
            .catch(() => setError('Entry not found'))
            .finally(() => setLoading(false));
    }, [slug]);

    // Record view once per mount
    useEffect(() => {
        if (!entry?.id || viewRecorded.current) return;
        viewRecorded.current = true;
        api.post(`/wiki/entries/${entry.id}/view`).catch(() => { });
    }, [entry]);

    if (loading) {
        return <div className="loading"><div className="spinner" />Loading entry...</div>;
    }

    if (error || !entry) {
        return (
            <div style={{ textAlign: 'center', padding: '4rem' }}>
                <h2 style={{ color: 'var(--error)' }}>Entry Not Found</h2>
                <p style={{ color: 'var(--text-secondary)' }}>{error || 'The requested entry does not exist.'}</p>
            </div>
        );
    }

    // Language-aware content (matching actual DB schema: title, overview_en/ov, caps_en/cv, full_text)
    const title = entry.title || 'Untitled';

    const overview = (lang === 'en' && entry.overview_en) || (lang === 'vi' && entry.overview_vi) || entry.overview_en || entry.full_text || '';

    const capabilities = (lang === 'en' && entry.capabilities_en) || (lang === 'vi' && entry.capabilities_vi) || entry.capabilities_en || '';

    const deploymentHistory = entry.deployment_history || '';

    const internalNotes = entry.internal_notes || entry.mike_notes || '';

    // Check lang availability chip
    const hasContentEn = !!(entry.overview_en || entry.capabilities_en);
    const hasContentVi = !!(entry.overview_vi || entry.capabilities_vi);
    const showOnlyChip = lang === 'en' && !hasContentEn && hasContentVi
        ? '[VI only]'
        : lang === 'vi' && !hasContentVi && hasContentEn
            ? '[EN only]'
            : null;

    // Parse client_fit JSON if string
    let clientFit = entry.client_fit;
    if (typeof clientFit === 'string') {
        try { clientFit = JSON.parse(clientFit); } catch (_) { clientFit = null; }
    }

    // Parse compliance JSON if string
    let compliance = entry.compliance;
    if (typeof compliance === 'string') {
        try { compliance = JSON.parse(compliance); } catch (_) { compliance = null; }
    }

    // Backend returns tags as objects: {id, slug, display_name, color}
    // WikiEntry passes them through to TagChips which handles both strings and objects
    const tags = Array.isArray(entry.tags) ? entry.tags : [];

    return (
        <div style={{ maxWidth: '900px' }}>
            {/* Breadcrumb */}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <a href="/wiki" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Wiki</a>
                <span>/</span>
                {entry.category && (
                    <>
                        <a href={`/wiki/category/${encodeURIComponent(entry.category.name || entry.category.slug || entry.category.id)}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                            {entry.category.name || entry.category.display_name || 'Category'}
                        </a>
                        <span>/</span>
                    </>
                )}
                <span style={{ color: 'var(--gold)' }}>{title}</span>
                {showOnlyChip && (
                    <span style={{ background: 'var(--bg-tertiary)', padding: '0 0.4rem', borderRadius: '3px' }}>
                        {showOnlyChip}
                    </span>
                )}
            </div>

            {/* Entry Header */}
            <EntryHeader entry={entry} />

            {/* Edit + Export buttons — editor+ */}
            <RoleGate min="editor">
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '1rem' }}>
                    <button onClick={() => navigate(`/wiki/${entry.id}/edit`)} style={{
                        background: 'rgba(212,175,55,0.15)', color: '#d4af37',
                        border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px',
                        padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                    }}>
                        ✏️ Edit
                    </button>
                    <ExportPdfButton entryId={entry.id} entrySlug={entry.slug} />
                </div>
            </RoleGate>

            {/* Stale indicator */}
            {entry.last_reviewed_at && <StaleIndicator lastReviewedAt={entry.last_reviewed_at} />}

            {/* Overview */}
            {overview && (
                <EntrySection title="Overview" icon={BookOpen}>
                    <MarkdownView content={overview} lang={lang} />
                </EntrySection>
            )}

            {/* Capabilities */}
            {capabilities && (
                <EntrySection title="Capabilities" icon={Zap}>
                    <MarkdownView content={capabilities} lang={lang} />
                </EntrySection>
            )}

            {/* Client Fit */}
            {clientFit && (
                <EntrySection title="Client Fit" icon={Users}>
                    <ClientFitPanel clientFit={clientFit} />
                </EntrySection>
            )}

            {/* Compliance */}
            {(entry.compliance || compliance) && (
                <EntrySection title="Compliance & Certifications" icon={Shield}>
                    <CompliancePanel compliance={compliance} />
                </EntrySection>
            )}

            {/* Deployment History */}
            {deploymentHistory && (
                <EntrySection title="Deployment History" icon={MapPin}>
                    <MarkdownView content={deploymentHistory} lang={lang} />
                </EntrySection>
            )}

            {/* Vendor */}
            {(entry.vendor_name || entry.vendor) && (
                <EntrySection title="Vendor" icon={Users}>
                    <VendorCard vendor={entry.vendor} entryVendor={entry.vendor_name} />
                </EntrySection>
            )}

            {/* Internal Notes — editor+ */}
            {internalNotes && (
                <RoleGate min="editor">
                    <EntrySection title="Internal Notes" icon={FileEdit} editorOnly={true} defaultOpen={false}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                            {internalNotes}
                        </div>
                    </EntrySection>
                </RoleGate>
            )}

            {/* Related Entries */}
            <EntrySection title="Related Entries" icon={BookOpen} defaultOpen={false}>
                <RelatedEntries entryId={entry.id} />
            </EntrySection>

            {/* Tags */}
            {tags.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Tags
                    </h4>
                    <TagChips tags={tags} />
                </div>
            )}
        </div>
    );
}